import { COOKIE_PREFERENCES_LABEL } from "@loot-os/api-contract";
import { cn } from "@loot-os/design-system/foundations/cn";
import { type ComponentProps, useEffect } from "react";

import { useTenant } from "../lib/tenant.js";
import { loadTermly, termlyOf } from "../lib/termly.js";

/**
 * The footer's "Cookie preferences" control, which also loads Termly's banner for a store that
 * resolves a Termly website. Renders nothing while the store has no website, which is also when
 * nothing Termly is loaded. The footer draws it as a link's `render`, so its label and classes
 * arrive as props.
 *
 * Termly wires a click handler onto the `termly-display-preferences` class when it finds the
 * element at init, but its observer never looks inside a React-rendered subtree, so the
 * `onClick` fallback is what actually opens the modal here. It is a `<button>` because Termly
 * disables the element when the website's preference widget is off.
 */
export function CookiePreferencesButton({
  className,
  children,
  ...props
}: ComponentProps<"button">) {
  const { config } = useTenant();
  const { websiteUuid } = termlyOf(config);

  // One script per page load: loadTermly ignores a second call, so StrictMode's doubled effect
  // and a later config refetch are no-ops. A change of website lands on the next load.
  useEffect(() => {
    if (websiteUuid) void loadTermly(websiteUuid);
  }, [websiteUuid]);

  if (!websiteUuid) return null;

  return (
    <button
      type="button"
      {...props}
      className={cn("termly-display-preferences", className)}
      onClick={() => window.displayPreferenceModal?.()}
    >
      {children ?? COOKIE_PREFERENCES_LABEL}
    </button>
  );
}
